import { isAgentPhoneWindow } from '../agentPhone/agentPhoneBridge';
import { AGENT_PHONE_WINDOW_NAME } from '../agentPhone/types';
import {
  PTT_AUDIO_LEADER_KEY,
  PTT_BRIDGE_CHANNEL,
  type PttBridgeMessage,
} from './types';

const TAB_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const LEADER_HEARTBEAT_MS = 2000;

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    channel = new BroadcastChannel(PTT_BRIDGE_CHANNEL);
  }
  return channel;
}

export function getPttTabId(): string {
  return TAB_ID;
}

type LeaderRecord = { tabId: string; at: number };

function readLeader(): LeaderRecord | null {
  try {
    const raw = localStorage.getItem(PTT_AUDIO_LEADER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LeaderRecord;
  } catch {
    return null;
  }
}

function writeLeader(tabId: string): void {
  localStorage.setItem(
    PTT_AUDIO_LEADER_KEY,
    JSON.stringify({ tabId, at: Date.now() } satisfies LeaderRecord)
  );
}

export function tryClaimPttAudioLeader(): boolean {
  if (isAgentPhoneWindow()) return false;
  const now = Date.now();
  const current = readLeader();
  const stale = !current || now - current.at > LEADER_HEARTBEAT_MS * 2;
  if (stale || current.tabId === TAB_ID) {
    writeLeader(TAB_ID);
    return true;
  }
  return current.tabId === TAB_ID;
}

export function isPttAudioLeader(): boolean {
  if (isAgentPhoneWindow()) return false;
  return readLeader()?.tabId === TAB_ID;
}

export function refreshPttAudioLeader(): void {
  if (isPttAudioLeader()) writeLeader(TAB_ID);
}

export function releasePttAudioLeader(): void {
  if (readLeader()?.tabId === TAB_ID) {
    localStorage.removeItem(PTT_AUDIO_LEADER_KEY);
  }
}

function postToPhonePopout(message: PttBridgeMessage): void {
  try {
    const phoneWin = window.open('', AGENT_PHONE_WINDOW_NAME);
    if (phoneWin && !phoneWin.closed) {
      phoneWin.postMessage(message, window.location.origin);
    }
  } catch {
    /* ignore */
  }
}

export function publishPttBridgeMessage(
  message:
    | { type: 'PTT_START'; channel_id: string; user_id: string; display_name: string; sample_rate: number; source_tab_id?: string }
    | { type: 'PTT_CHUNK'; channel_id: string; user_id: string; chunk: string; seq: number; source_tab_id?: string }
    | { type: 'PTT_END'; channel_id: string; user_id: string; source_tab_id?: string }
    | { type: 'PTT_PHONE_READY'; source_tab_id?: string }
): void {
  const payload = {
    ...message,
    source_tab_id: message.source_tab_id ?? TAB_ID,
  } as PttBridgeMessage;

  getChannel()?.postMessage(payload);
  if (!isAgentPhoneWindow()) {
    postToPhonePopout(payload);
  }
}

export function publishPttPhoneReady(): void {
  publishPttBridgeMessage({ type: 'PTT_PHONE_READY' });
}

export function subscribePttBridgeMessages(
  handler: (message: PttBridgeMessage) => void
): () => void {
  const bc = getChannel();
  const onBc = (event: MessageEvent) => handler(event.data as PttBridgeMessage);
  bc?.addEventListener('message', onBc);

  const onWindow = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as PttBridgeMessage;
    if (!data?.type) return;
    handler(data);
  };
  window.addEventListener('message', onWindow);

  return () => {
    bc?.removeEventListener('message', onBc);
    window.removeEventListener('message', onWindow);
  };
}
